import AppModule from '@app/lib/AppModule';
import IAppEvents from '@app/lib/IAppEvents';
import IAppLogger from '@app/lib/IAppLogger';
import environment from '@lib/environment';
import appManager from '@app/appManager';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import { PendoEventProperties, PendoEventsType } from './PendoEvents';

declare global {
  interface Window {
    pendo: any;
  }
}

export class PendoModule extends AppModule {
  private loadingPromise?: Promise<void>;

  constructor(events: IAppEvents, appLogger: IAppLogger) {
    super(events, appLogger);
  }

  private get pendo() {
    return (window as any).pendo;
  }

  override preload(): void {
    this.app.events.on('app.started', async () => {
      if (environment.PENDO_API_KEY) {
        try {
          this.loadingPromise ??= this.loadPendoSDK();
          await this.loadingPromise; // Wait for SDK to load
          this.app.logger.info('Pendo SDK ready');
        } catch (error) {
          this.app.logger.warn('Pendo SDK failed to load on startup:', error);
        }
      } else {
        this.app.logger.warn('Pendo not initialized: missing API key');
      }
    });

    this.app.events.on(AuthEvents.SIGNIN, async (user: AuthUser) => {
      try {
        // Ensure SDK is loaded
        this.loadingPromise ??= this.loadPendoSDK();
        await this.loadingPromise; // Wait for SDK to load

        if (this.pendo) {
          this.pendo.initialize({
            visitor: {
              id: user.id,
              name: user.name,
              email: user.email,
              accountId: user.accountId,
              contactCenterId: user.contactCenterId,
              orgId: user.org.id,
              orgName: user.org.name,
            },
            account: {
              id: user.org.id,
              name: user.org.name,
            },
          });
          this.app.logger.info('Initialized with user: ', user.id);
        }
      } catch (error) {
        this.app.logger.warn('Initialization failed: ', error);
      }
    });

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      if (this.pendo) {
        this.pendo.identify({ visitor: { id: null }, account: { id: null } });
        this.app.logger.info('User signed out');
      }
    });
  }

  public trackEvent<T extends PendoEventsType>(
    eventName: T,
    properties?: PendoEventProperties[T],
  ): void {
    if (!this.pendo) {
      this.app.logger.warn('trackEvent: Pendo is not initialized.');
      return;
    }
    const props: Record<string, any> = properties || {};
    props.timestamp = new Date().toISOString();
    this.pendo?.track(eventName, props);
  }

  public flushEvents() {
    if (!this.pendo) {
      this.app.logger.warn('flushEvents: Pendo is not initialized.');
      return;
    }
    this.pendo?.flushNow?.(true);
  }

  // Loads Pendo SDK script
  private async loadPendoSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://cdn.pendo.io/agent/static/${environment.PENDO_API_KEY}/pendo.js`;
      script.async = true;
      script.onload = () => {
        this.app.logger.info('Pendo SDK loaded');
        resolve();
      };
      script.onerror = () => {
        this.app.logger.error('Failed to load Pendo SDK.');
        reject(new Error('Failed to load Pendo SDK.'));
      };
      document.head.appendChild(script);
    });
  }
}

export default new PendoModule(appManager.events, appManager.logger('Pendo'));
